import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, Skeleton, Typography } from '@mui/material';
import { MdArrowBack } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import ProductForm from '../../components/Products/ProductForm';
import { applyFormChange, authToken, buildProductFormData, FONT, formFromProduct, initialFormState, yellowBtn } from '../../components/Products/productShared';
import { getData, putData } from '../../Webservice/Webservice';
import PRODUCT_ROUTES from '../../API_Route/ProductRoute';

const EditProduct = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(initialFormState);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState('');

    const fetchCategories = useCallback(async () => {
        try {
            const res = await getData('categories/list?limit=1000', null, authToken(), false);
            setCategories(Array.isArray(res?.categories) ? res.categories : []);
        } catch { setCategories([]); }
    }, []);

    const fetchSubcategories = useCallback(async (categoryId) => {
        if (!categoryId) { setSubcategories([]); return; }
        try {
            const res = await getData(`subcategories/list?limit=1000&category_id=${categoryId}`, null, authToken(), false);
            setSubcategories(Array.isArray(res?.subcategories) ? res.subcategories : []);
        } catch { setSubcategories([]); }
    }, []);

    useEffect(() => {
        fetchCategories();
        setLoading(true);
        getData(PRODUCT_ROUTES.GET_PRODUCT_DETAILS + productId, null, authToken(), false)
            .then((res) => {
                const p = res?.product || res?.data || res;
                if (!p?._id) throw new Error('missing');
                const next = formFromProduct(p);
                setForm(next);
                fetchSubcategories(next.category_id);
            })
            .catch(() => setLoadError('This product could not be loaded. It may have been deleted.'))
            .finally(() => setLoading(false));
    }, [productId, fetchCategories, fetchSubcategories]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => applyFormChange(f, name, value));
        if (name === 'category_id') fetchSubcategories(value);
    };

    const handleUpdateProduct = async () => {
        setSubmitting(true);
        setServerError('');
        try {
            await putData(`product/update/${productId}`, buildProductFormData(form, { isEdit: true }), authToken(), false);
            navigate('/productmanagement', { state: { notice: `“${form.title.trim()}” was updated.` } });
        } catch (error) {
            setServerError(error?.message || 'Could not update the product.');
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={90} sx={{ borderRadius: '14px', mb: 2 }} /><Skeleton variant="rounded" height={420} sx={{ borderRadius: '14px' }} /></Box>;
    if (loadError) {
        return (
            <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
                <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
                <Button onClick={() => navigate('/productmanagement')} sx={yellowBtn}>Back to products</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <IconButton onClick={() => navigate('/productmanagement')} sx={{ background: '#f1f5f9' }}><MdArrowBack size={18} /></IconButton>
                <Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Edit product</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>{form.productCode ? `${form.productCode} · ` : ''}{form.title}</Typography>
                </Box>
            </Box>
            <ProductForm
                form={form}
                onFormChange={handleChange}
                onCancel={() => navigate('/productmanagement')}
                onSubmit={handleUpdateProduct}
                categories={categories}
                subcategories={subcategories}
                isEdit
                fetchCategories={fetchCategories}
                fetchSubcategories={fetchSubcategories}
                submitting={submitting}
                serverError={serverError}
            />
        </Box>
    );
};

export default EditProduct;
